import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StateService {
  user: User;

  private vehiclesByServiceType: {[serviceTypeId: number]: VehicleData[]} = {};
  private stationsByServiceType: {[serviceTypeId: number]: NumDict} = {};
  private districtsByServiceType: {[serviceTypeId: number]: NumDict} = {};
  private accessGroupsByServiceType: {[serviceTypeId: number]: AccessGroup[]} = {};

  constructor(protected client: HttpClient) { }

  getUserInfo() {
    return this.client.get<User>('/api/me').pipe(
      tap(
        x => this.user = x
      )
    );
  }

  logout() {
    return this.client.delete<{logoutUrl?: string}>('/api/Auth').pipe(
      tap(
        x => {
          this.user = undefined;
          if (x && x.logoutUrl) {
            window.location.assign(x.logoutUrl);
          }
        }
      ));
  }

  getVehicles(serviceTypeId: number) {
    const vehicles = this.vehiclesByServiceType[serviceTypeId];
    if (vehicles) {
      return of(vehicles);
    }
    return this.client.get<VehicleData[]>(`/api/categories/vehicles?serviceTypeId=${serviceTypeId}`)
      .pipe(tap(x => this.vehiclesByServiceType[serviceTypeId] = x));
  }

  getStations(serviceTypeId: number) {
    const stations = this.stationsByServiceType[serviceTypeId];
    if (stations) {
      return of(stations);
    }
    return this.client.get<StringDict>(`/api/categories/stations?serviceTypeId=${serviceTypeId}`).pipe(map(x => {
      const d: NumDict = {};
      for (const k of Object.keys(x)) {
        d[+k] = x[k];
      }
      this.stationsByServiceType[serviceTypeId] = d;
      return d;
    }));
  }

  getDistricts(serviceTypeId: number) {
    const districts = this.districtsByServiceType[serviceTypeId];
    if (districts) {
      return of(districts);
    }
    return this.client.get<StringDict>(`/api/categories/districts?serviceTypeId=${serviceTypeId}`).pipe(map(x => {
      const d: NumDict = {};
      for (const k of Object.keys(x)) {
        d[+k] = x[k];
      }
      this.districtsByServiceType[serviceTypeId] = d;
      return d;
    }));
  }

  getAccessGroups(serviceTypeId: number) {
    const accessGroups = this.accessGroupsByServiceType[serviceTypeId];
    if (accessGroups) {
      return of(accessGroups);
    }
    return this.client.get<AccessGroup[]>(`/api/categories/access-groups?serviceTypeId=${serviceTypeId}`).pipe(tap(x => {
      this.accessGroupsByServiceType[serviceTypeId] = x;
    }));
  }
}

interface StringDict {
  [key: string]: string;
}

export interface NumDict {
  [key: number]: string;
}

export interface User {
  accessKeys: string[];
  serviceTypes: {[serviceTypeId: number]: string};
}

export interface VehicleData {
  callsign: string;
  stationId: number | null;
  districtId: number;
}

export interface AccessGroup {
  id: number;
  name: string;
  requiredFields: string[];
  optionalFields: string[];
  conditionallyRequiredFields: string[];
}